import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import AttendeesAndNextMeetingModal from '../../components/AttendeesAndNextMeetingModal';
import axiosInstance from '../../utils/axiosInstance';

const ACTION_ITEM_TEMPLATE_HEADERS = ['Description', 'Assigned Staff', 'Deadline Date (YYYY-MM-DD)', 'Remark'];

function downloadActionItemsTemplate() {
  const worksheet = XLSX.utils.aoa_to_sheet([
    ACTION_ITEM_TEMPLATE_HEADERS,
    ['Example: Follow up with batch coordinator', 'Jane Doe', '2026-09-01', 'Optional notes'],
  ]);
  worksheet['!cols'] = [{ wch: 50 }, { wch: 24 }, { wch: 24 }, { wch: 34 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Action Items');
  XLSX.writeFile(workbook, 'Action_Items_Template.xlsx');
}

const STATUS_META = {
  pending: { label: 'Pending', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  completed: { label: 'Completed', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  deadline_extended: { label: 'Deadline Extended', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  carried_over: { label: 'Carried Over', badge: 'bg-slate-100 text-slate-500 border-slate-200' },
};
// Only these are user-selectable in the status dropdown -- 'carried_over' is
// a system-set state applied only when a task is duplicated into a later
// meeting (see the "Grab Pending Tasks" flow).
const EDITABLE_STATUSES = ['pending', 'completed', 'deadline_extended'];

const GRID_CELL = 'border border-slate-200 px-2 py-1.5';
const DEFAULT_BLANK_ROWS = 6;

// Borderless, compact styling so the search-to-filter dropdown sits flush
// inside a spreadsheet-style table cell instead of looking like a normal
// form field.
const CELL_SELECT_STYLES = {
  container: (base) => ({ ...base, minWidth: 140 }),
  control: (base, state) => ({
    ...base,
    minHeight: '28px',
    border: 'none',
    borderRadius: '0.25rem',
    background: 'transparent',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(99,102,241,0.25)' : 'none',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 4px' }),
  indicatorsContainer: (base) => ({ ...base, height: '28px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '0.8125rem' }),
  placeholder: (base) => ({ ...base, fontSize: '0.8125rem', color: '#94a3b8' }),
  singleValue: (base) => ({ ...base, fontSize: '0.8125rem' }),
  menuPortal: (base) => ({ ...base, zIndex: 70 }),
  menu: (base) => ({ ...base, fontSize: '0.8125rem', minWidth: 200 }),
};

function fmtDate(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(`${value}T00:00:00`));
}

function to12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  let hours = parseInt(h, 10);
  const modifier = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${modifier}`;
}

let rowKeySeq = 0;
function newBlankRow() {
  rowKeySeq += 1;
  return { key: `new-${rowKeySeq}`, description: '', assignee: null, deadlineDate: '', remark: '' };
}

// A saved action item, rendered as an editable spreadsheet-style row --
// every field change goes straight to the server via PATCH (this is for
// items that already exist; brand-new rows are handled separately by
// NewItemsTable so they can be entered fast and saved together in one bulk
// request).
function SavedItemRow({ item, staffOptions, onUpdate, onDelete, showOrigin, rowNumber, justAdded }) {
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingField, setEditingField] = useState(null); // 'description' | 'remark' | null
  const assignee = item.assignedStaffId ? { value: item.assignedStaffId, label: item.assignedStaffName } : null;

  const flashSaved = () => {
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2200);
  };

  // A row freshly created by the bulk "Save New Items" action arrives here
  // already persisted -- flash it too, so it's clear at a glance which rows
  // just landed in the table.
  useEffect(() => {
    if (justAdded) flashSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justAdded]);

  const save = async (patch) => {
    setBusy(true);
    try {
      await onUpdate(item.id, patch);
      flashSaved();
    } finally {
      setBusy(false);
    }
  };

  // Enter (no shift) commits by blurring the field -- its onBlur does the
  // actual save; Escape backs out without saving, discarding the edit.
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      setEditingField(null);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Remove this action item?',
      text: `"${item.description}" will be permanently deleted. This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeleting(true);
    try {
      await onDelete(item.id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <tr className={`transition-colors duration-700 ${justSaved ? 'bg-emerald-50' : deleting ? 'bg-red-50 opacity-50' : 'bg-white'}`}>
      <td className={`${GRID_CELL} w-10 bg-slate-50 text-center text-xs font-semibold text-slate-400`}>
        {justSaved ? (
          <span className="inline-flex items-center text-emerald-600" title="Saved">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
          </span>
        ) : rowNumber}
      </td>
      <td className={`${GRID_CELL} text-sm text-slate-800`}>
        {editingField === 'description' ? (
          <textarea
            autoFocus
            defaultValue={item.description}
            rows={2}
            disabled={busy}
            onFocus={(e) => e.target.select()}
            onBlur={(e) => {
              const trimmed = e.target.value.trim();
              if (trimmed && trimmed !== item.description) save({ description: trimmed });
              setEditingField(null);
            }}
            onKeyDown={handleEditKeyDown}
            className="w-full resize-none rounded border border-indigo-300 bg-white px-1.5 py-1 text-sm outline-none"
          />
        ) : (
          <div
            className="flex cursor-text items-start gap-1.5"
            onDoubleClick={() => setEditingField('description')}
            title="Double-click to edit"
          >
            <span className="min-w-0">{item.description}</span>
            {item.duplicateCount > 0 && (
              <span
                title={`Carried over ${item.duplicateCount} time${item.duplicateCount === 1 ? '' : 's'} without being completed`}
                className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold text-orange-700"
              >
                <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>
                {item.duplicateCount}
              </span>
            )}
          </div>
        )}
        {showOrigin && <p className="mt-0.5 text-[11px] text-slate-400">From "{item.raisedInMeetingTitle}" · {fmtDate(item.raisedInMeetingDate)}</p>}
      </td>
      <td className={GRID_CELL}>
        <Select
          value={assignee}
          onChange={(v) => save({ assignedStaffId: v?.value || null })}
          options={staffOptions}
          isClearable
          isDisabled={busy}
          placeholder="Unassigned"
          styles={CELL_SELECT_STYLES}
          menuPortalTarget={document.body}
        />
      </td>
      <td className={GRID_CELL}>
        <input
          type="date"
          defaultValue={item.deadlineDate || ''}
          onBlur={(e) => save({ deadlineDate: e.target.value || null })}
          disabled={busy}
          className="w-full bg-transparent text-sm outline-none disabled:opacity-50"
        />
      </td>
      <td className={GRID_CELL}>
        {item.status === 'carried_over' ? (
          <span
            title="This task was duplicated into a later meeting and is tracked there now"
            className={`inline-block w-full rounded px-1 py-0.5 text-center text-xs font-semibold ${STATUS_META.carried_over.badge}`}
          >
            {STATUS_META.carried_over.label}
          </span>
        ) : (
          <select
            value={item.status}
            onChange={(e) => save({ status: e.target.value })}
            disabled={busy}
            className={`w-full rounded px-1 py-0.5 text-xs font-semibold ${STATUS_META[item.status].badge}`}
          >
            {EDITABLE_STATUSES.map((value) => (
              <option key={value} value={value}>{STATUS_META[value].label}</option>
            ))}
          </select>
        )}
      </td>
      <td className={GRID_CELL}>
        {editingField === 'remark' ? (
          <textarea
            autoFocus
            defaultValue={item.remark || ''}
            rows={2}
            disabled={busy}
            onFocus={(e) => e.target.select()}
            onBlur={(e) => {
              const trimmed = e.target.value.trim();
              if (trimmed !== (item.remark || '')) save({ remark: trimmed || null });
              setEditingField(null);
            }}
            onKeyDown={handleEditKeyDown}
            className="w-full resize-none rounded border border-indigo-300 bg-white px-1.5 py-1 text-sm outline-none"
          />
        ) : (
          <div
            className="cursor-text text-sm text-slate-600"
            onDoubleClick={() => setEditingField('remark')}
            title="Double-click to edit"
          >
            {item.remark || <span className="text-slate-300">Double-click to add a remark…</span>}
          </div>
        )}
      </td>
      <td className={`${GRID_CELL} w-10 text-center`}>
        <button
          type="button"
          onClick={handleDelete}
          disabled={busy || deleting}
          title="Remove this action item"
          className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      </td>
    </tr>
  );
}

// Fast, Excel-style bulk-entry grid: several blank rows are ready to fill
// immediately, Tab moves cell-to-cell in natural DOM order, and Enter in the
// description cell of the last row opens another one. Nothing hits the
// server until "Save New Items" -- one request inserts every filled-in row.
function NewItemsTable({ staffOptions, onSaveRows }) {
  const [rows, setRows] = useState(() => Array.from({ length: DEFAULT_BLANK_ROWS }, newBlankRow));
  const [saving, setSaving] = useState(false);

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, newBlankRow()]);

  const removeRow = (key) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  const handleDescriptionKeyDown = (e, isLastRow) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isLastRow) addRow();
    }
  };

  const filledRows = rows.filter((r) => r.description.trim());

  const save = async () => {
    if (filledRows.length === 0) return;
    setSaving(true);
    try {
      await onSaveRows(filledRows.map((r) => ({
        description: r.description.trim(),
        assignedStaffId: r.assignee?.value || null,
        deadlineDate: r.deadlineDate || null,
        remark: r.remark.trim() || null,
      })));
      setRows(Array.from({ length: DEFAULT_BLANK_ROWS }, newBlankRow));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
            <th className={`${GRID_CELL} w-10 bg-slate-50`}>#</th>
            <th className={`${GRID_CELL} bg-slate-50`}>Description</th>
            <th className={`${GRID_CELL} w-48 bg-slate-50`}>Assigned To</th>
            <th className={`${GRID_CELL} w-40 bg-slate-50`}>Deadline</th>
            <th className={`${GRID_CELL} bg-slate-50`}>Remark</th>
            <th className={`${GRID_CELL} w-10 bg-slate-50`} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.key} className="bg-white">
              <td className={`${GRID_CELL} text-center text-xs font-semibold text-slate-400`}>{idx + 1}</td>
              <td className={GRID_CELL}>
                <textarea
                  value={row.description}
                  onChange={(e) => updateRow(row.key, { description: e.target.value })}
                  onKeyDown={(e) => handleDescriptionKeyDown(e, idx === rows.length - 1)}
                  rows={1}
                  placeholder={idx === 0 ? 'Type an action item, press Enter for a new row…' : ''}
                  className="w-full resize-none bg-transparent text-sm outline-none"
                />
              </td>
              <td className={GRID_CELL}>
                <Select
                  value={row.assignee}
                  onChange={(v) => updateRow(row.key, { assignee: v })}
                  options={staffOptions}
                  isClearable
                  placeholder="Assign to…"
                  styles={CELL_SELECT_STYLES}
                  menuPortalTarget={document.body}
                />
              </td>
              <td className={GRID_CELL}>
                <input
                  type="date"
                  value={row.deadlineDate}
                  onChange={(e) => updateRow(row.key, { deadlineDate: e.target.value })}
                  className="w-full bg-transparent text-sm outline-none"
                />
              </td>
              <td className={GRID_CELL}>
                <textarea
                  value={row.remark}
                  onChange={(e) => updateRow(row.key, { remark: e.target.value })}
                  rows={1}
                  placeholder="Optional remark…"
                  className="w-full resize-none bg-transparent text-sm outline-none"
                />
              </td>
              <td className={`${GRID_CELL} text-center`}>
                <button
                  onClick={() => removeRow(row.key)}
                  disabled={rows.length === 1}
                  className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-red-500 disabled:opacity-30"
                  aria-label="Remove row"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between border border-t-0 border-slate-200 bg-slate-50 px-3 py-2">
        <button onClick={addRow} className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Add Row
        </button>
        <button
          onClick={save}
          disabled={saving || filledRows.length === 0}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {saving ? 'Saving…' : `Save New Item${filledRows.length === 1 ? '' : 's'}${filledRows.length > 0 ? ` (${filledRows.length})` : ''}`}
        </button>
      </div>
    </div>
  );
}

// Download-a-template / upload-it-back counterpart to NewItemsTable, for
// preparing a large batch of action items offline in Excel instead of typing
// them into the grid. Parsing and per-row validation happen client-side
// (mirrors UploadStudents.jsx) so bad rows are caught before anything hits
// the network; only the rows with zero errors get sent, in one request, to
// the same bulk endpoint the inline grid already uses.
function UploadActionItemsModal({ staffOptions, onSaveRows, onClose }) {
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const staffIdByName = useMemo(() => {
    const map = new Map();
    staffOptions.forEach((s) => map.set(String(s.label).trim().toLowerCase(), s.value));
    return map;
  }, [staffOptions]);

  const handleFile = async (file) => {
    setParseError('');
    setParsedRows([]);
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, blankrows: false });

      if (matrix.length === 0) {
        setParseError('The file is empty.');
        return;
      }
      const firstHeaderCell = String(matrix[0][0] || '').trim().toLowerCase();
      if (firstHeaderCell !== 'description') {
        setParseError(`First column must be "Description" (found "${matrix[0][0] || ''}"). Please use the downloaded template.`);
        return;
      }

      const rows = matrix.slice(1).map((cells, idx) => {
        const rowNumber = idx + 2; // header is row 1
        const description = String(cells[0] || '').trim();
        const assigneeName = String(cells[1] || '').trim();
        const deadlineDateRaw = String(cells[2] || '').trim();
        const remark = String(cells[3] || '').trim();

        const errors = [];
        if (!description) errors.push('Missing description');

        let staffId = null;
        if (assigneeName) {
          staffId = staffIdByName.get(assigneeName.toLowerCase()) || null;
          if (!staffId) errors.push(`Staff "${assigneeName}" not found`);
        }

        let deadlineDate = null;
        if (deadlineDateRaw) {
          const validDate = /^\d{4}-\d{2}-\d{2}$/.test(deadlineDateRaw) && !Number.isNaN(new Date(deadlineDateRaw).getTime());
          if (validDate) deadlineDate = deadlineDateRaw;
          else errors.push(`Invalid date "${deadlineDateRaw}" (use YYYY-MM-DD)`);
        }

        return { rowNumber, description, assigneeName, staffId, deadlineDate, remark, errors };
      }).filter((r) => r.description || r.assigneeName || r.deadlineDate || r.remark);

      if (rows.length === 0) {
        setParseError('No data rows found below the header.');
        return;
      }
      setParsedRows(rows);
    } catch (err) {
      setParseError('Could not read this file. Make sure it is a valid .xlsx file.');
    }
  };

  const validRows = parsedRows.filter((r) => r.errors.length === 0);
  const invalidRows = parsedRows.filter((r) => r.errors.length > 0);

  const handleInsert = async () => {
    if (validRows.length === 0) return;
    setSubmitting(true);
    try {
      await onSaveRows(validRows.map((r) => ({
        description: r.description,
        assignedStaffId: r.staffId,
        deadlineDate: r.deadlineDate,
        remark: r.remark || null,
      })));
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-800">Upload Action Items</h3>
            <p className="mt-1 text-xs text-slate-500">Fill in the downloaded template, then upload it here to bulk-insert action items.</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100" aria-label="Close">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="mt-4 rounded-xl border-2 border-dashed border-slate-200 p-5 text-center">
          <input
            id="action-items-upload-input"
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          <label
            htmlFor="action-items-upload-input"
            className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2z" /></svg>
            Choose file…
          </label>
          {fileName && <p className="mt-2 text-xs text-slate-500">{fileName}</p>}
        </div>

        {parseError && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{parseError}</p>}

        {parsedRows.length > 0 && (
          <div className="mt-4">
            <div className="mb-2 flex items-center gap-3 text-xs font-semibold">
              <span className="text-emerald-600">{validRows.length} ready to insert</span>
              {invalidRows.length > 0 && <span className="text-red-600">{invalidRows.length} will be skipped</span>}
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    <th className="px-2 py-1.5">Row</th>
                    <th className="px-2 py-1.5">Description</th>
                    <th className="px-2 py-1.5">Assignee</th>
                    <th className="px-2 py-1.5">Deadline</th>
                    <th className="px-2 py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.map((r) => (
                    <tr key={r.rowNumber} className={r.errors.length > 0 ? 'bg-red-50' : 'bg-white'}>
                      <td className="px-2 py-1.5 text-slate-400">{r.rowNumber}</td>
                      <td className="max-w-[220px] truncate px-2 py-1.5 text-slate-700" title={r.description}>{r.description || '—'}</td>
                      <td className="px-2 py-1.5 text-slate-700">{r.assigneeName || '—'}</td>
                      <td className="px-2 py-1.5 text-slate-700">{r.deadlineDate || '—'}</td>
                      <td className="px-2 py-1.5">
                        {r.errors.length === 0 ? (
                          <span className="font-semibold text-emerald-600">Ready</span>
                        ) : (
                          <span className="font-semibold text-red-600" title={r.errors.join('; ')}>{r.errors.join('; ')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={submitting} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button
            onClick={handleInsert}
            disabled={submitting || validRows.length === 0}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submitting ? 'Inserting…' : `Insert ${validRows.length} Item${validRows.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Lets the super admin pick still-open tasks raised in OTHER meetings and
// bring them into this one -- each pick creates a fresh row here (tracked
// independently going forward) while the source row is marked carried_over,
// so it stops showing up as grabbable a second time.
function GrabPendingTasksModal({ meetingId, onClose, onDuplicated }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axiosInstance.get(`/admin/meetings/${meetingId}/pending-tasks`)
      .then((resp) => setTasks(resp.data?.tasks || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const toggle = (taskId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const allSelected = tasks.length > 0 && selectedIds.size === tasks.length;
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(tasks.map((t) => t.id)));
  };

  const confirm = async () => {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    try {
      await onDuplicated([...selectedIds]);
      onClose();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to bring in the selected tasks.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-slate-800">Grab Pending Tasks</h3>
            <p className="mt-0.5 text-xs text-slate-400">Still-open tasks from past meetings. Pick the ones to bring into this meeting.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <svg className="h-7 w-7 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
          ) : tasks.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">No open pending tasks from other meetings.</p>
          ) : (
            <>
              <label className="mb-2 flex w-fit items-center gap-2 text-xs font-semibold text-indigo-600 hover:text-indigo-800">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && !allSelected; }}
                  onChange={toggleAll}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                />
                {allSelected ? 'Uncheck all' : 'Check all'} ({tasks.length})
              </label>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {tasks.map((task) => (
                <li key={task.id} className="flex items-start gap-3 px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(task.id)}
                    onChange={() => toggle(task.id)}
                    className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-slate-800">{task.description}</span>
                      {task.duplicateCount > 0 && (
                        <span
                          title={`Already carried over ${task.duplicateCount} time${task.duplicateCount === 1 ? '' : 's'}`}
                          className="inline-flex items-center gap-0.5 rounded-full border border-orange-200 bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold text-orange-700"
                        >
                          ↻ {task.duplicateCount}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                      <span className="font-semibold text-indigo-600">{task.raisedInMeetingTitle}</span>
                      <span>{fmtDate(task.raisedInMeetingDate)}</span>
                      {task.assignedStaffName && <span>Assigned: {task.assignedStaffName}</span>}
                      {task.deadlineDate && <span>Deadline: {fmtDate(task.deadlineDate)}</span>}
                    </div>
                  </div>
                </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-5 py-4">
          <span className="text-xs text-slate-500">{selectedIds.size} selected</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button
              onClick={confirm}
              disabled={submitting || selectedIds.size === 0}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting ? 'Importing…' : `Import Selected (${selectedIds.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// "Dear A," / "Dear A and B," / "Dear A, B, and C," -- the Oxford comma before
// the last name is what keeps a 3+ list from reading like a run-on.
function buildSalutation(names) {
  if (names.length === 0) return 'Dear Team,';
  if (names.length === 1) return `Dear ${names[0]},`;
  if (names.length === 2) return `Dear ${names[0]} and ${names[1]},`;
  return `Dear ${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]},`;
}

// Generates a professional, ready-to-copy email (recipients + subject + body)
// for sending this meeting's minutes to its attendees. There's no email-send
// integration in this app, so like "Generate Invite" on the meetings list,
// this only prepares text to paste into your own email client -- the PDF
// itself is the existing "Generate PDF" print view, linked here so both
// pieces of the workflow live in one place.
function GenerateEmailModal({ meeting, allStaff, onClose }) {
  const [copiedSubject, setCopiedSubject] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState(false);
  const [copiedEmails, setCopiedEmails] = useState(false);
  const [copiedNames, setCopiedNames] = useState(false);

  const staffById = useMemo(() => {
    const map = new Map();
    allStaff.forEach((s) => map.set(s.staffId, s));
    return map;
  }, [allStaff]);

  const attendees = useMemo(() => meeting.attendees || [], [meeting.attendees]);
  const recipients = useMemo(() => attendees.map((a) => {
    const staff = staffById.get(a.staffId);
    return {
      staffId: a.staffId,
      name: a.name,
      officeEmail: staff?.officeEmail || null,
      gmail: staff?.gmail || null,
    };
  }), [attendees, staffById]);
  const recipientEmails = useMemo(
    () => recipients.map((s) => s.officeEmail || s.gmail).filter(Boolean),
    [recipients]
  );

  const subject = `Meeting Minutes — ${meeting.title} (${fmtDate(meeting.meetingDate)})`;

  // A short, skimmable preview -- a one-line gist of the notes plus an
  // action-item headcount -- so the recipient gets the key points straight
  // from the email, without needing to open the attached PDF first.
  const shortSummary = useMemo(() => {
    const notes = (meeting.notes || '').replace(/\s+/g, ' ').trim();
    if (!notes) return '';
    return notes.length > 200 ? `${notes.slice(0, 197)}...` : notes;
  }, [meeting.notes]);

  const actionItemsLine = useMemo(() => {
    const items = meeting.actionItems || [];
    if (items.length === 0) return '';
    const pending = items.filter((i) => i.status === 'pending' || i.status === 'deadline_extended').length;
    const completed = items.filter((i) => i.status === 'completed').length;
    return `Action items: ${items.length} raised — ${pending} pending, ${completed} completed.`;
  }, [meeting.actionItems]);

  const message = useMemo(() => {
    const dateLine = `${fmtDate(meeting.meetingDate)}${meeting.meetingTime ? ` at ${to12h(meeting.meetingTime)}` : ''}`;
    const salutation = buildSalutation(attendees.map((a) => a.name));
    const lines = [
      salutation,
      '',
      `Please find attached the minutes of the meeting "${meeting.title}", held on ${dateLine}${meeting.location ? ` at ${meeting.location}` : ''}.`,
      '',
      shortSummary ? `Summary: ${shortSummary}` : null,
      actionItemsLine || null,
      (shortSummary || actionItemsLine) ? '' : null,
      'Kindly review the attached minutes (PDF) at your convenience, and let us know if you have any questions or if any corrections are needed.',
      '',
      'Thank you.',
    ].filter((line) => line !== null);
    return lines.join('\n');
  }, [meeting, attendees, shortSummary, actionItemsLine]);

  const copySubject = async () => {
    await navigator.clipboard.writeText(subject);
    setCopiedSubject(true);
    setTimeout(() => setCopiedSubject(false), 2000);
  };
  const copyMessage = async () => {
    await navigator.clipboard.writeText(message);
    setCopiedMessage(true);
    setTimeout(() => setCopiedMessage(false), 2000);
  };
  const copyEmails = async () => {
    await navigator.clipboard.writeText(recipientEmails.join(', '));
    setCopiedEmails(true);
    setTimeout(() => setCopiedEmails(false), 2000);
  };
  const copyNames = async () => {
    await navigator.clipboard.writeText(recipients.map((s) => s.name).join(', '));
    setCopiedNames(true);
    setTimeout(() => setCopiedNames(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-800">Generate Email</h3>
            <p className="mt-1 text-xs text-slate-500">For this meeting's attendees, with the minutes as an attachment</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100" aria-label="Close">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-600">Recipients ({recipients.length})</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={copyNames}
                  disabled={recipients.length === 0}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {copiedNames ? 'Copied!' : 'Copy names'}
                </button>
                <button
                  type="button"
                  onClick={copyEmails}
                  disabled={recipientEmails.length === 0}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {copiedEmails ? 'Copied!' : 'Copy emails'}
                </button>
              </div>
            </div>
            {recipients.length === 0 ? (
              <p className="text-xs text-slate-400">No attendees added yet — use "Attendees &amp; Next Meeting" to add some first.</p>
            ) : (
              <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-2">
                {recipients.map((s) => (
                  <span
                    key={s.staffId}
                    title={s.officeEmail || s.gmail || 'No email on file'}
                    className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600"
                  >
                    {s.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-600">Subject</label>
              <button type="button" onClick={copySubject} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                {copiedSubject ? 'Copied!' : 'Copy subject'}
              </button>
            </div>
            <input
              readOnly
              value={subject}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 focus:outline-none"
            />
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-600">Email message</label>
              <button type="button" onClick={copyMessage} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                {copiedMessage ? 'Copied!' : 'Copy message'}
              </button>
            </div>
            <textarea
              readOnly
              value={message}
              rows={10}
              className="w-full resize-y rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-700 focus:outline-none"
            />
          </div>

          <a
            href={`/admin/meetings/${meeting.id}/print`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659" /></svg>
            Download Meeting Minutes (PDF)
          </a>
        </div>

        <div className="mt-5 flex justify-end">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Close</button>
        </div>
      </div>
    </div>
  );
}

export default function MeetingEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [notes, setNotes] = useState('');
  const [inviteNotes, setInviteNotes] = useState('');
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [showGrabModal, setShowGrabModal] = useState(false);
  const [showUploadActionItems, setShowUploadActionItems] = useState(false);
  const [showGenerateEmail, setShowGenerateEmail] = useState(false);
  const [recentlyAddedIds, setRecentlyAddedIds] = useState(() => new Set());

  // `silent` skips the full-page loading state (used for manual refresh, so
  // the already-visible content doesn't disappear behind a spinner) and
  // shows a small inline spinner on the Refresh button instead.
  const load = (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    axiosInstance.get(`/admin/meetings/${id}`)
      .then((resp) => {
        const m = resp.data?.meeting || null;
        setMeeting(m);
        setNotes(m?.notes || '');
        setInviteNotes(m?.inviteNotes || '');
      })
      .catch((err) => {
        if (silent) {
          // Keep whatever's already on screen -- just let them know the
          // refresh itself failed, don't blank out good data over it.
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: err?.response?.data?.message || 'Failed to refresh.',
            showConfirmButton: false,
            timer: 2200,
          });
        } else {
          setMeeting(null);
        }
      })
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => {
    axiosInstance.get('/admin/staff').then((resp) => {
      const staff = resp.data?.staff || [];
      const activeStaff = staff.filter((s) => (s.loginStatus || 'active') === 'active');
      setStaffOptions(activeStaff.map((s) => ({ value: s.staffId, label: s.name })));
      setAllStaff(activeStaff);
    }).catch(() => { setStaffOptions([]); setAllStaff([]); });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateActionItem = async (itemId, patch) => {
    try {
      await axiosInstance.patch(`/admin/meetings/${id}/action-items/${itemId}`, patch);
      load(true);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to update action item.', 'error');
    }
  };

  const deleteActionItem = async (itemId) => {
    try {
      const resp = await axiosInstance.delete(`/admin/meetings/${id}/action-items/${itemId}`);
      setMeeting(resp.data?.meeting || null);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Action item deleted',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete action item.', 'error');
    }
  };

  const saveNewItems = async (items) => {
    try {
      const resp = await axiosInstance.post(`/admin/meetings/${id}/action-items/bulk`, { items });
      const updatedMeeting = resp.data?.meeting || null;
      setMeeting(updatedMeeting);

      // actionItems is ordered by createdAt ASC, so the rows just inserted
      // are the last `insertedCount` of them -- flash exactly those.
      const insertedCount = resp.data?.insertedCount || 0;
      if (updatedMeeting && insertedCount > 0) {
        const newIds = updatedMeeting.actionItems.slice(-insertedCount).map((i) => i.id);
        setRecentlyAddedIds(new Set(newIds));
        setTimeout(() => setRecentlyAddedIds(new Set()), 2200);
      }

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `${insertedCount} item${insertedCount === 1 ? '' : 's'} saved`,
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to save action items.', 'error');
    }
  };

  const duplicateTasks = async (itemIds) => {
    const resp = await axiosInstance.post(`/admin/meetings/${id}/action-items/duplicate`, { itemIds });
    const updatedMeeting = resp.data?.meeting || null;
    setMeeting(updatedMeeting);

    const duplicatedCount = resp.data?.duplicatedCount || 0;
    if (updatedMeeting && duplicatedCount > 0) {
      const newIds = updatedMeeting.actionItems.slice(-duplicatedCount).map((i) => i.id);
      setRecentlyAddedIds(new Set(newIds));
      setTimeout(() => setRecentlyAddedIds(new Set()), 2200);
    }

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: `${duplicatedCount} task${duplicatedCount === 1 ? '' : 's'} brought into this meeting`,
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  };

  const [notesSaving, setNotesSaving] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      await axiosInstance.patch(`/admin/meetings/${id}`, { notes });
      setNotesDirty(false);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Notes saved',
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to save notes.', 'error');
    } finally {
      setNotesSaving(false);
    }
  };

  const [inviteNotesSaving, setInviteNotesSaving] = useState(false);
  const [inviteNotesDirty, setInviteNotesDirty] = useState(false);
  const saveInviteNotes = async () => {
    setInviteNotesSaving(true);
    try {
      await axiosInstance.patch(`/admin/meetings/${id}`, { inviteNotes });
      setInviteNotesDirty(false);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Invite notes saved',
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to save invite notes.', 'error');
    } finally {
      setInviteNotesSaving(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-slate-400">Loading…</div>;
  }
  if (!meeting) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p>Meeting not found.</p>
        <button onClick={() => navigate('/admin/meetings')} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">Back to Meetings</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 py-3 shadow-sm">
        <button onClick={() => navigate('/admin/meetings')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
          Back to Meetings
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            title="Refetch this meeting's data"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <svg className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <a
            href={`/admin/meetings/${id}/print`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659" /></svg>
            Generate PDF
          </a>
          <button
            type="button"
            onClick={() => setShowGenerateEmail(true)}
            title="Generate a professional email to this meeting's attendees, with the minutes as an attachment"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
            Generate Email
          </button>
        </div>
      </div>

      <div className="w-full px-6 py-6">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-8 py-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold text-slate-900">{meeting.title}</h1>
                <p className="mt-1 text-sm text-slate-500">
                  {fmtDate(meeting.meetingDate)}{meeting.meetingTime ? ` · ${to12h(meeting.meetingTime)}` : ''}
                  {meeting.location ? ` · ${meeting.location}` : ''}
                  {meeting.chairedByName ? ` · Chaired by ${meeting.chairedByName}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <span><span className="font-semibold text-slate-800">{meeting.attendees.filter((a) => a.attended === 1).length}/{meeting.attendees.length}</span> attended</span>
                {meeting.nextMeetingDate && (
                  <span className="border-l border-slate-300 pl-2">Next: <span className="font-semibold text-indigo-600">{fmtDate(meeting.nextMeetingDate)}{meeting.nextMeetingTime ? ` · ${to12h(meeting.nextMeetingTime)}` : ''}</span></span>
                )}
                <button
                  onClick={() => setShowAttendeesModal(true)}
                  title="Manage attendees and next meeting"
                  className="ml-1 rounded-md border border-indigo-200 bg-white px-2 py-1 font-semibold text-indigo-700 hover:bg-indigo-50"
                >
                  Manage
                </button>
              </div>
            </div>
          </div>

          <div className="px-8 py-6">
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">This Meeting's Action Items</h2>
                <button
                  onClick={() => setShowGrabModal(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25z" /></svg>
                  Grab Pending Tasks
                </button>
              </div>
              {meeting.actionItems.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="text-left text-[11px] font-bold uppercase tracking-wide text-slate-500">
                        <th className={`${GRID_CELL} w-10 bg-slate-50`}>#</th>
                        <th className={`${GRID_CELL} bg-slate-50`}>Description</th>
                        <th className={`${GRID_CELL} w-48 bg-slate-50`}>Assigned To</th>
                        <th className={`${GRID_CELL} w-40 bg-slate-50`}>Deadline</th>
                        <th className={`${GRID_CELL} w-36 bg-slate-50`}>Status</th>
                        <th className={`${GRID_CELL} bg-slate-50`}>Remark</th>
                        <th className={`${GRID_CELL} w-10 bg-slate-50`} />
                      </tr>
                    </thead>
                    <tbody>
                      {meeting.actionItems.map((item, idx) => (
                        <SavedItemRow
                          key={item.id}
                          item={item}
                          staffOptions={staffOptions}
                          onUpdate={updateActionItem}
                          onDelete={deleteActionItem}
                          rowNumber={idx + 1}
                          justAdded={recentlyAddedIds.has(item.id)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-3">
                <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold text-slate-400">Add new items — fill in the rows below like a spreadsheet, then save them all at once.</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={downloadActionItemsTemplate}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
                      Download Template
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowUploadActionItems(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2z" /></svg>
                      Upload Filled Template
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <NewItemsTable staffOptions={staffOptions} onSaveRows={saveNewItems} />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Notes</label>
                {notesDirty && !notesSaving && <span className="text-[11px] text-amber-600">Unsaved changes</span>}
              </div>
              <textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
                onBlur={saveNotes}
                rows={4}
                placeholder="General minutes / notes…"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
              <div className="mt-2 flex justify-end">
                <button
                  onClick={saveNotes}
                  disabled={notesSaving || !notesDirty}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {notesSaving ? 'Saving…' : 'Save Notes'}
                </button>
              </div>
            </div>

            <div className="mt-6">
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Invite Notes</label>
                {inviteNotesDirty && !inviteNotesSaving && <span className="text-[11px] text-amber-600">Unsaved changes</span>}
              </div>
              <p className="mb-1.5 text-[11px] text-slate-400">Agenda shown in the "Generate Invite" message — kept separate from the Notes above.</p>
              <textarea
                value={inviteNotes}
                onChange={(e) => { setInviteNotes(e.target.value); setInviteNotesDirty(true); }}
                onBlur={saveInviteNotes}
                rows={4}
                placeholder="Agenda for the invite email…"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
              <div className="mt-2 flex justify-end">
                <button
                  onClick={saveInviteNotes}
                  disabled={inviteNotesSaving || !inviteNotesDirty}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {inviteNotesSaving ? 'Saving…' : 'Save Invite Notes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAttendeesModal && (
        <AttendeesAndNextMeetingModal
          meetingId={id}
          staffOptions={staffOptions}
          onClose={() => setShowAttendeesModal(false)}
          onChanged={() => load(true)}
        />
      )}

      {showGrabModal && (
        <GrabPendingTasksModal
          meetingId={id}
          onClose={() => setShowGrabModal(false)}
          onDuplicated={duplicateTasks}
        />
      )}

      {showUploadActionItems && (
        <UploadActionItemsModal
          staffOptions={staffOptions}
          onSaveRows={saveNewItems}
          onClose={() => setShowUploadActionItems(false)}
        />
      )}

      {showGenerateEmail && (
        <GenerateEmailModal
          meeting={meeting}
          allStaff={allStaff}
          onClose={() => setShowGenerateEmail(false)}
        />
      )}
    </div>
  );
}
